import React, { useCallback, useState } from 'react';
import DeleteOutlineIcon from '@mui/icons-material//DeleteOutline';
import PortraitIcon from '@mui/icons-material//Portrait';
import { Typography } from '@mui/material';
import Slider from '@mui/material/Slider';
import { Grid } from '@mui/system';
import clsx from 'clsx';
import Cropper, { Area } from 'react-easy-crop';
import { PROFILE_PICTURE, PROFILE_PICTURE_SPECS } from 'common/constants/user';
import { DropZone } from 'components/';
import { TitleArray } from 'components/CircleIconWithText/types';
import { validImageExtensions } from 'components/Documents/imageTypes';
import { FileSrcProps } from 'components/DropZone/DropZone.props';
import { ImageContainerProps } from './ImageContainer.props';
import { classes, Root } from './ImageContainer.styles';


const readFile = async (
  file: File,
  onLoadCallback: (element: { target: { result: string } }) => void
): Promise<void> => {
  const reader = new FileReader();
  reader.onload = () => onLoadCallback({ target: { result: reader.result as string } });
  reader.readAsDataURL(file);
};

const ImageContainer = ({
  imageSrc,
  setImageSrc,
  setSelectedFile,
  setCroppedAreaPixels,
  title,
  cropShape,
  aspect,
}: ImageContainerProps) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);

  const onCropComplete = useCallback(
    (_croppedArea: Area, completeCroppedAreaPixels: Area) => {
      setCroppedAreaPixels(completeCroppedAreaPixels);
    },
    [setCroppedAreaPixels]
  );

  const processImage = async ({ file }: FileSrcProps) => {
    setSelectedFile(file ?? null);
    await readFile(file ?? new File([], ''), element => {
      const { result } = element.target;
      setImageSrc(result);
    });
  };

  const resetImage = () => {
    setSelectedFile(null);
    setImageSrc(null);
  };

  const titleArray: TitleArray = [
    [`Drag & Drop ${title ?? PROFILE_PICTURE}`, {}],
    ['or', {}],
    ['Select a file', { textDecoration: 'underline' }],
  ];

  const setHtmlColor = (condition: boolean): string => {
    const defaultColor = classes.defaultHtmlColor as unknown as { color: string };
    return condition ? defaultColor.color : 'lightgrey';
  };

  const getAspect = (): number => {
    if (aspect) {
      return aspect.horizontal / aspect.vertical;
    }
    return 1;
  };

  return (
    <Root>
      <Typography variant="h6">{title ?? PROFILE_PICTURE}</Typography>
      <br />
      <Grid container spacing={2} direction="row" justifyContent="center" alignItems="center">
        <Grid size={{ sm: 12 }}>
          {imageSrc ? (
            <div className={classes.imageSrcContainer}>
              <div className={classes.cropContainer}>
                <Cropper
                  image={imageSrc}
                  crop={crop}
                  zoom={zoom}
                  aspect={getAspect()}
                  cropShape={cropShape ?? 'round'}
                  onCropChange={setCrop}
                  onCropComplete={onCropComplete}
                  onZoomChange={setZoom}
                  showGrid={false}
                  minZoom={0.1}
                  restrictPosition={false}
                />
              </div>
              <div className={classes.controls}>
                <div className={classes.sliderContainer}>
                  <Grid container spacing={2}>
                    <Grid>
                      <PortraitIcon
                        className={clsx(classes.handlerIcons, classes.smallPortraitIcon)}
                        fontSize="small"
                        htmlColor={setHtmlColor(zoom > 1)}
                      />
                    </Grid>
                    <Grid size={{ xs: true }}>
                      <Slider
                        value={zoom}
                        min={0.1}
                        max={3}
                        step={0.1}
                        aria-labelledby="Zoom"
                        classes={{ root: classes.slider }}
                        onChange={(_e: React.ChangeEvent<unknown>, sliderZoom: number | number[]) =>
                          setZoom(sliderZoom as number)
                        }
                      />
                    </Grid>
                    <Grid>
                      <PortraitIcon
                        className={classes.handlerIcons}
                        fontSize="medium"
                        htmlColor={setHtmlColor(zoom < 3)}
                      />
                    </Grid>
                    <Grid>
                      <DeleteOutlineIcon
                        className={`${classes.handlerIcons} ${classes.pointer}`}
                        fontSize="medium"
                        onClick={resetImage}
                        htmlColor={setHtmlColor(true)}
                      />
                    </Grid>
                  </Grid>
                </div>
              </div>
            </div>
          ) : (
            <DropZone
              setFileSrc={processImage}
              subTitle={PROFILE_PICTURE_SPECS}
              acceptFileTypes="image/*"
              validFileTypes={validImageExtensions}
              maxSizeAllowedInMb={10}
              titleArray={titleArray}
              allowMultipleFiles={false}
            />
          )}
        </Grid>
      </Grid>
    </Root>
  );
};

export default ImageContainer;
